import { supabase } from '../lib/supabase';
import {
  CohortPatientOverview,
  ControlPlace,
  GlucoseTargets,
  GlucoseThresholds,
  MonthlyReport,
  MonthlyReportSummary,
  PatientProgress,
  SocioEconomicData
} from '../types/domain';
import { Profile } from '../react/context/AuthContext';
import { dataService, mapTargetsRow } from './dataService';
import { mapOverviewRow } from '../lib/cohort';
import { mapReportRow, monthStart } from '../lib/monthlyReport';
import type { ExportClinicalRow, ExportLogRow } from '../lib/export/exportData';
import type { AuditEntry } from '../lib/audit';

/** A place a doctor is assigned to. `id` is null only for a legacy row the
 *  DF-09 backfill could not resolve. */
export interface AssignedPlace {
  id: string | null;
  name: string;
}

export interface DuplicateGroup {
  core: string;
  variant_count: number;
  names: string[];
  ids: string[];
}

/** PostgREST returns at most 1,000 rows per request unless the project
 *  raises it; the export pages at that size so it never silently stops. */
export const EXPORT_PAGE_SIZE = 1000;
/** Patient ids per `.in()` filter — keeps the request URL short. */
const EXPORT_ID_CHUNK = 100;

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

/**
 * Mirrors the CHECK constraints on glucose_targets so a bad range is refused
 * with a readable Indonesian message instead of a Postgres constraint error.
 * The database keeps its own constraints — this is the friendly half.
 */
function assertThresholds(t: GlucoseThresholds, label: string): void {
  const values = [t.low, t.normalMax, t.warningMax];
  if (values.some(v => !Number.isFinite(v) || !Number.isInteger(v))) {
    throw new Error(`Nilai target ${label} harus berupa angka bulat.`);
  }
  if (!(t.low < t.normalMax && t.normalMax < t.warningMax)) {
    throw new Error(
      `Target ${label} harus menaik: batas rendah < batas aman < batas waspada.`
    );
  }
  if (t.low < 20 || t.warningMax > 600) {
    throw new Error(`Target ${label} harus berada di antara 20 dan 600 mg/dL.`);
  }
}

export const adminService = {
  /**
   * The managed hospital list (DF-09).
   *
   * This replaces getDistinctControlPlaces(), which derived the admin's
   * assignment dropdown from whatever patients had typed into a free-text
   * field. That was a feedback loop: one patient typo manufactured a new
   * hospital for the admin to pick, and a doctor assigned to the correctly
   * spelled one never saw that patient. The list is now its own table.
   */
  async getControlPlaces(): Promise<ControlPlace[]> {
    const { data, error } = await supabase
      .from('control_places')
      .select('id, name, status')
      .order('name', { ascending: true });

    if (error) throw error;
    return (data ?? []) as ControlPlace[];
  },

  async createControlPlace(name: string): Promise<ControlPlace> {
    const clean = name.trim().replace(/\s+/g, ' ');
    if (!clean) throw new Error('Nama tempat kontrol tidak boleh kosong.');

    const { data, error } = await supabase
      .from('control_places')
      .insert({ name: clean, status: 'active' })
      .select('id, name, status')
      .single();

    if (error) throw error;
    return data as ControlPlace;
  },

  async renameControlPlace(placeId: string, name: string): Promise<void> {
    const clean = name.trim().replace(/\s+/g, ' ');
    if (!clean) throw new Error('Nama tempat kontrol tidak boleh kosong.');

    const { error } = await supabase
      .from('control_places')
      .update({ name: clean })
      .eq('id', placeId);

    if (error) throw error;
    await adminService.syncPlaceNameCache(placeId, clean);
  },

  /** Approves a place a patient created via "Lainnya". */
  async approveControlPlace(placeId: string): Promise<void> {
    const { error } = await supabase
      .from('control_places')
      .update({ status: 'active' })
      .eq('id', placeId);

    if (error) throw error;
  },

  /**
   * Merges one place into another: repoints every patient and doctor
   * assignment, then deletes the now-empty source.
   *
   * This is the manual half of the de-duplication. The migration only
   * auto-merged names identical after trim/case-folding; deciding that
   * "RS Soetomo" and "RSUD Dr. Soetomo" are the same hospital is an
   * administrative judgement, so it happens here.
   */
  async mergeControlPlaces(sourceId: string, targetId: string): Promise<void> {
    if (sourceId === targetId) {
      throw new Error('Tidak bisa menggabungkan tempat kontrol dengan dirinya sendiri.');
    }

    const { data: target, error: tError } = await supabase
      .from('control_places')
      .select('id, name')
      .eq('id', targetId)
      .single();
    if (tError) throw tError;

    const { error: pError } = await supabase
      .from('profiles')
      .update({ hospital_id: targetId, control_place: target.name })
      .eq('hospital_id', sourceId);
    if (pError) throw pError;

    // Re-point assignments, skipping any the doctor already holds on the
    // target — the (doctor_id, control_place_id) unique index would reject it.
    const { data: sourceLinks, error: sError } = await supabase
      .from('doctor_control_places')
      .select('id, doctor_id')
      .eq('control_place_id', sourceId);
    if (sError) throw sError;

    const { data: targetLinks, error: tlError } = await supabase
      .from('doctor_control_places')
      .select('doctor_id')
      .eq('control_place_id', targetId);
    if (tlError) throw tlError;

    const alreadyOnTarget = new Set((targetLinks ?? []).map(l => l.doctor_id));

    for (const link of sourceLinks ?? []) {
      if (alreadyOnTarget.has(link.doctor_id)) {
        await supabase.from('doctor_control_places').delete().eq('id', link.id);
      } else {
        await supabase
          .from('doctor_control_places')
          .update({ control_place_id: targetId, control_place: target.name })
          .eq('id', link.id);
      }
    }

    const { error: dError } = await supabase
      .from('control_places')
      .delete()
      .eq('id', sourceId);
    if (dError) throw dError;
  },

  /** Keeps the denormalized display copies in step with the canonical name. */
  async syncPlaceNameCache(placeId: string, name: string): Promise<void> {
    await supabase.from('profiles').update({ control_place: name }).eq('hospital_id', placeId);
    await supabase
      .from('doctor_control_places')
      .update({ control_place: name })
      .eq('control_place_id', placeId);
  },

  /** Near-duplicates the migration deliberately did not merge. */
  async getPossibleDuplicates(): Promise<DuplicateGroup[]> {
    const { data, error } = await supabase
      .from('control_places_possible_duplicates')
      .select('*');

    if (error) throw error;
    return (data ?? []) as unknown as DuplicateGroup[];
  },

  async getDoctors(): Promise<(Profile & { assigned_places: AssignedPlace[] })[]> {
    const { data: doctorsData, error: dError } = await supabase
      .from('profiles')
      .select('*')
      .eq('role', 'doctor');
      
    if (dError) throw dError;

    if (doctorsData.length === 0) return [];

    // Scoped to the doctors just fetched (NFR-02). This used to pull every
    // assignment row in the table and discard the ones that did not match.
    const { data: placesData, error: pError } = await supabase
      .from('doctor_control_places')
      .select('doctor_id, control_place, control_place_id, control_places(name)')
      .in('doctor_id', doctorsData.map((d: any) => d.id));

    if (pError) throw pError;

    return doctorsData.map((doc: any) => {
      return {
        ...doc,
        assigned_places: (placesData as any[])
          .filter(p => p.doctor_id === doc.id)
          .map(p => ({
            id: p.control_place_id as string | null,
            // Prefer the joined canonical name; fall back to the denormalized
            // copy for any row not yet backfilled.
            name: p.control_places?.name ?? p.control_place
          }))
      };
    });
  },

  async assignDoctorToControlPlace(doctorId: string, controlPlaceId: string): Promise<void> {
    const { data: place, error: pError } = await supabase
      .from('control_places')
      .select('name')
      .eq('id', controlPlaceId)
      .single();
    if (pError) throw pError;

    const { error } = await supabase
      .from('doctor_control_places')
      .insert({
        doctor_id: doctorId,
        control_place_id: controlPlaceId,
        // Denormalized display copy; control_place_id is the matching key.
        control_place: place.name
      });

    if (error) throw error;
  },

  async removeDoctorFromControlPlace(doctorId: string, controlPlaceId: string): Promise<void> {
    const { error } = await supabase
      .from('doctor_control_places')
      .delete()
      .match({ doctor_id: doctorId, control_place_id: controlPlaceId });

    if (error) throw error;
  },

  async getAllPatients(): Promise<Profile[]> {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('role', 'patient')
      .order('full_name', { ascending: true });
    if (error) throw error;
    return data as Profile[];
  },

  async getPatientsForDoctor(doctorId: string): Promise<Profile[]> {
    const { data: places, error: pError } = await supabase
      .from('doctor_control_places')
      .select('control_place_id')
      .eq('doctor_id', doctorId)
      .not('control_place_id', 'is', null);

    if (pError) throw pError;
    // Matched by id since DF-09. This used to compare free-text strings, so
    // "RSUD Dr. Soetomo" and "rsud dr. soetomo" were different hospitals and a
    // single typo made a patient invisible to their doctor.
    const allowedPlaceIds = places.map(p => p.control_place_id as string);

    if (allowedPlaceIds.length === 0) return [];

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('role', 'patient')
      .in('hospital_id', allowedPlaceIds)
      .order('full_name', { ascending: true });
      
    if (error) throw error;
    return data as Profile[];
  },

  /** The single global target row every patient inherits (FR-06). */
  async getGlobalTargets(): Promise<GlucoseTargets | null> {
    const { data, error } = await supabase
      .from('glucose_targets')
      .select('*')
      .eq('scope', 'global')
      .maybeSingle();

    if (error) throw error;
    return data ? mapTargetsRow(data) : null;
  },

  /** A patient's override, if a doctor has set one. */
  async getPatientTargets(patientId: string): Promise<GlucoseTargets | null> {
    const { data, error } = await supabase
      .from('glucose_targets')
      .select('*')
      .eq('patient_id', patientId)
      .maybeSingle();

    if (error) throw error;
    return data ? mapTargetsRow(data) : null;
  },

  /**
   * Writes the global defaults. Admin-facing.
   *
   * Only the numbers are writable: the four clinician-approved message strings
   * are stored and versioned alongside them but never edited here, because
   * what a child reads about hypoglycemia is a clinical artefact rather than a
   * settings field.
   */
  async updateGlobalTargets(
    pre: GlucoseThresholds,
    post: GlucoseThresholds,
    updatedBy: string
  ): Promise<void> {
    assertThresholds(pre, 'pra-makan');
    assertThresholds(post, 'pasca-makan');

    const { error } = await supabase
      .from('glucose_targets')
      .update({
        pre_low: pre.low,
        pre_normal_max: pre.normalMax,
        pre_warning_max: pre.warningMax,
        post_low: post.low,
        post_normal_max: post.normalMax,
        post_warning_max: post.warningMax,
        updated_by: updatedBy,
        updated_at: new Date().toISOString()
      })
      .eq('scope', 'global');

    if (error) throw error;
  },

  /** Creates or replaces one patient's override. Doctor- and admin-facing. */
  async upsertPatientTargets(
    patientId: string,
    pre: GlucoseThresholds,
    post: GlucoseThresholds,
    updatedBy: string
  ): Promise<void> {
    assertThresholds(pre, 'pra-makan');
    assertThresholds(post, 'pasca-makan');

    const existing = await adminService.getPatientTargets(patientId);
    const payload = {
      scope: 'patient',
      patient_id: patientId,
      pre_low: pre.low,
      pre_normal_max: pre.normalMax,
      pre_warning_max: pre.warningMax,
      post_low: post.low,
      post_normal_max: post.normalMax,
      post_warning_max: post.warningMax,
      updated_by: updatedBy,
      updated_at: new Date().toISOString()
    };

    const { error } = existing
      ? await supabase.from('glucose_targets').update(payload).eq('id', existing.id)
      : await supabase.from('glucose_targets').insert(payload);

    if (error) throw error;
  },

  /** Drops an override so the patient falls back to the global defaults. */
  async clearPatientTargets(patientId: string): Promise<void> {
    const { error } = await supabase
      .from('glucose_targets')
      .delete()
      .eq('patient_id', patientId)
      .eq('scope', 'patient');

    if (error) throw error;
  },

  /**
   * The doctor's cohort, aggregated over a trailing window (FR-14).
   *
   * One round trip for the whole list. The alternative — a history fetch per
   * patient, then re-banding each reading in JS — is the N+1 shape NFR-02
   * names, and it would also have to re-resolve every patient's FR-06
   * thresholds client-side to know what "hypo" meant for that child.
   *
   * `doctorId` null asks for every patient, which is the superadmin's view.
   */
  async getCohortOverview(
    doctorId: string | null,
    days = 14
  ): Promise<CohortPatientOverview[]> {
    const { data, error } = await supabase.rpc('doctor_patient_overview', {
      p_doctor_id: doctorId,
      p_days: days
    });

    if (error) throw error;
    return ((data ?? []) as any[]).map(mapOverviewRow);
  },

  /**
   * One patient's live chart data (FR-14).
   *
   * Distinct from getPatientMonthlyReport(): that returns a frozen snapshot of
   * a calendar month, this returns the trailing window as it stands now. Both
   * exist on purpose — the doctor asks "how is this child doing lately" far
   * more often than "what did March say".
   */
  async getPatientProgress(
    patientId: string,
    days = 30
  ): Promise<PatientProgress | null> {
    const { data, error } = await supabase.rpc('patient_progress', {
      p_user_id: patientId,
      p_days: days
    });

    if (error) throw error;
    // The function returns JSONB, so the generated type is `Json`. The shape is
    // PatientProgress by construction — `version` is what a reader checks.
    return (data as unknown as PatientProgress) ?? null;
  },

  /**
   * A patient's raw log, newest first.
   *
   * Bounded: the table underneath it is a scroll area, not an export, and a
   * child three years into the app has thousands of rows. FR-18's export is
   * what pulls the whole log.
   */
  async getPatientHistory(patientId: string, limit = 300): Promise<any[]> {
    const { data, error } = await supabase
      .from('injection_history')
      .select('*')
      .eq('user_id', patientId)
      .order('date', { ascending: false })
      .order('time', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data;
  },

  /**
   * Everything FR-18 exports for a set of patients, optionally within a date
   * range (inclusive; both columns are `date`, so ISO strings compare exactly).
   *
   * The caller passes ids from the patient list the viewer already has, which
   * is what scopes a doctor to their own hospitals — the same client-side
   * boundary as the rest of the dashboard (A1).
   *
   * Paged, not limited: this is the one read that must return a patient's
   * whole history. Ordered by a unique key so a page boundary cannot skip or
   * repeat a row.
   */
  async getExportData(
    patientIds: string[],
    range: { from: string; to: string } | null
  ): Promise<{ logs: ExportLogRow[]; clinical: ExportClinicalRow[] }> {
    const logs: ExportLogRow[] = [];
    const clinical: ExportClinicalRow[] = [];

    for (const ids of chunk(patientIds, EXPORT_ID_CHUNK)) {
      for (let offset = 0; ; offset += EXPORT_PAGE_SIZE) {
        let query = supabase
          .from('injection_history')
          .select(
            'id, user_id, date, time, glucose_value, measurement_context, insulin_dose, ' +
              'insulin_brand, age_group, site_id, region_id, point_row, point_col, ' +
              'lipodystrophy, insulin_products(name)'
          )
          .in('user_id', ids);
        if (range) {
          query = query.gte('date', range.from).lte('date', range.to);
        }
        const { data, error } = await query
          .order('id', { ascending: true })
          .range(offset, offset + EXPORT_PAGE_SIZE - 1);
        if (error) throw error;

        const page = (data ?? []) as any[];
        for (const row of page) {
          logs.push({
            userId: row.user_id,
            date: row.date,
            time: row.time ?? '',
            glucoseValue: row.glucose_value ?? null,
            measurementContext: row.measurement_context ?? null,
            insulinDose: row.insulin_dose ?? null,
            // The catalog name when the row links a product (FR-04), else the
            // free text older rows stored.
            insulinName: row.insulin_products?.name ?? row.insulin_brand ?? null,
            ageGroup: row.age_group,
            siteId: row.site_id ?? null,
            regionId: row.region_id ?? null,
            pointRow: row.point_row ?? null,
            pointCol: row.point_col ?? null,
            lipodystrophy: row.lipodystrophy === true
          });
        }
        if (page.length < EXPORT_PAGE_SIZE) break;
      }

      for (let offset = 0; ; offset += EXPORT_PAGE_SIZE) {
        let query = supabase
          .from('clinical_records')
          .select('id, user_id, recorded_at, weight_kg, height_cm, hba1c')
          .in('user_id', ids);
        if (range) {
          query = query.gte('recorded_at', range.from).lte('recorded_at', range.to);
        }
        const { data, error } = await query
          .order('id', { ascending: true })
          .range(offset, offset + EXPORT_PAGE_SIZE - 1);
        if (error) throw error;

        const page = (data ?? []) as any[];
        for (const row of page) {
          clinical.push({
            userId: row.user_id,
            recordedAt: row.recorded_at,
            weightKg: row.weight_kg ?? null,
            heightCm: row.height_cm ?? null,
            hba1c: row.hba1c ?? null
          });
        }
        if (page.length < EXPORT_PAGE_SIZE) break;
      }
    }

    return { logs, clinical };
  },

  /**
   * The newest audit entries (FR-22), optionally for one day range and one
   * patient. Bounded: this is a screen to look something up on, and the log
   * grows with every reading.
   */
  async getAuditLog(filters: {
    from?: string;
    to?: string;
    patientId?: string;
    limit?: number;
  }): Promise<AuditEntry[]> {
    let query = supabase.from('audit_log').select('*');
    if (filters.from) {
      query = query.gte('occurred_at', `${filters.from}T00:00:00`);
    }
    if (filters.to) {
      query = query.lte('occurred_at', `${filters.to}T23:59:59.999`);
    }
    if (filters.patientId) {
      query = query.eq('patient_id', filters.patientId);
    }
    const { data, error } = await query
      .order('occurred_at', { ascending: false })
      .limit(filters.limit ?? 200);
    if (error) throw error;

    return ((data ?? []) as any[]).map(row => ({
      id: row.id,
      occurredAt: row.occurred_at,
      actorId: row.actor_id,
      actorRole: row.actor_role,
      action: row.action,
      entity: row.entity,
      entityId: row.entity_id,
      patientId: row.patient_id,
      details: (row.details ?? {}) as Record<string, unknown>
    }));
  },

  /** Display names for a set of profile ids, for the audit viewer. */
  async getProfileNames(ids: string[]): Promise<Record<string, string>> {
    const unique = Array.from(new Set(ids.filter(Boolean)));
    if (unique.length === 0) {
      return {};
    }
    const { data, error } = await supabase
      .from('profiles')
      .select('id, full_name')
      .in('id', unique);
    if (error) throw error;
    return Object.fromEntries(
      ((data ?? []) as any[]).map(row => [row.id as string, (row.full_name as string) ?? '—'])
    );
  },

  /** Sets an admin-managed switch (migration 014). Superadmin only, by UI. */
  async setAppSetting(key: string, value: unknown, adminId: string): Promise<void> {
    const { error } = await supabase
      .from('app_settings')
      .upsert(
        { key, value: value as any, updated_at: new Date().toISOString(), updated_by: adminId },
        { onConflict: 'key' }
      );
    if (error) throw error;
  },

  /** One patient's profile, for a screen reached by id rather than by list. */
  async getPatientProfile(patientId: string): Promise<Profile | null> {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', patientId)
      .maybeSingle();

    if (error) throw error;
    return (data as Profile) ?? null;
  },

  /**
   * Edits one patient's FR-03 socio-economic fields.
   *
   * FR-03 says these are editable "by patient and admin", so the write is the
   * same one the patient performs — delegated rather than duplicated, because
   * two copies of the normalise-then-validate step is exactly how the stored
   * shape starts to differ depending on who typed it.
   *
   * There is no server-side check that the caller is an admin (A1). The
   * Dashboard only offers the editor to a superadmin; that gate is the whole
   * control.
   */
  async updatePatientSocioEconomic(
    patientId: string,
    data: SocioEconomicData
  ): Promise<void> {
    await dataService.updateSocioEconomic(patientId, data);
  },

  /**
   * One patient's stored monthly report (FR-16).
   *
   * The doctor reads the same snapshot the patient does — not a fresh
   * aggregation — so the two never discuss different numbers for the same
   * month. Returns null when the cron job has not produced that month yet;
   * dataService.generateMonthlyReport() is what fills the gap.
   */
  async getPatientMonthlyReport(
    patientId: string,
    month: string
  ): Promise<MonthlyReport | null> {
    const { data, error } = await supabase
      .from('monthly_reports')
      .select('*')
      .eq('user_id', patientId)
      .eq('period_month', monthStart(month))
      .maybeSingle();

    if (error) throw error;
    return data ? mapReportRow(data) : null;
  },

  /** Which months this patient already has a report for, newest first. */
  async listPatientMonthlyReports(patientId: string): Promise<MonthlyReportSummary[]> {
    const { data, error } = await supabase
      .from('monthly_reports')
      .select('period_month, generated_at')
      .eq('user_id', patientId)
      .order('period_month', { ascending: false });

    if (error) throw error;
    return (data ?? []).map((row: any) => ({
      periodMonth: row.period_month,
      generatedAt: row.generated_at
    }));
  },

  /**
   * Creates a doctor account (DF-07).
   *
   * Delegates to the create-doctor Edge Function, which holds the service-role
   * key. The previous implementation called client-side signUp() and then
   * tried to put the admin's session back afterwards — which breaks outright
   * once email confirmation is on, because no session exists for the new user,
   * and ran the profiles insert as the wrong principal.
   *
   * Nothing here signs in, so the admin's session is never disturbed. The
   * function also re-checks that the caller is a superadmin: with RLS excluded
   * (URS A1) the endpoint cannot rely on the database to refuse anyone else.
   */
  async createDoctorUser(email: string, password: string, fullName: string): Promise<void> {
    const { data, error } = await supabase.functions.invoke('create-doctor', {
      body: { email, password, fullName }
    });

    if (error) {
      // The function returns a readable Indonesian message in the body; surface
      // that rather than "Edge Function returned a non-2xx status code".
      let message = 'Gagal membuat akun dokter.';
      const context = (error as any).context;
      if (context && typeof context.json === 'function') {
        try {
          const body = await context.json();
          if (body?.error) {
            message = body.error;
          }
        } catch {
          // Fall through to the generic message.
        }
      }
      throw new Error(message);
    }

    if ((data as any)?.error) {
      throw new Error((data as any).error);
    }
  }
};

